import { useEffect, useMemo, useRef, useState } from 'react';

import { type Items } from './DetailsNavigation';

interface UseStickProps {
  items: Items;
}

const OFFSET = -50;

export default function useDetailsNavigation(props: Readonly<UseStickProps>) {
  const { items } = props;

  const [activeKey, setActiveKey] = useState<string | undefined>(items[0]?.key);

  const isIntersectingRefValue = useMemo(
    () =>
      items.reduce<Partial<Record<string, boolean>>>((acc, item) => {
        acc[item.key] = false;

        return acc;
      }, {}),
    [items]
  );

  const isIntersectingRef = useRef(isIntersectingRefValue);

  useEffect(() => {
    isIntersectingRef.current = isIntersectingRefValue;
  }, [isIntersectingRefValue]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isIntersectingRef.current[entry.target.id] = entry.isIntersecting;
        });

        const intersectingElements = Object.keys(
          isIntersectingRef.current
        ).filter((item) => isIntersectingRef.current[item]);

        if (intersectingElements.length > 0) {
          setActiveKey(intersectingElements[0]);
        }
      },
      {
        rootMargin: `${OFFSET}px 0px 0px 0px`,
      }
    );

    items.forEach((item) => {
      if (item.ref.current) {
        observer.observe(item.ref.current);
      }
    });

    return () => observer.disconnect();
  }, [items]);

  return {
    activeKey,
  };
}
